/**
 * Guest Recent Analyses - localStorage-based history for non-authenticated users
 * 
 * For guests, we store up to 10 recent analyses in localStorage.
 * This provides a seamless experience even without authentication.
 */

const STORAGE_KEY = 'chekdeal_recent_analyses';
const MAX_GUEST_ANALYSES = 10;

/**
 * Get all recent analyses for guest user
 * @returns {Array} Array of recent analysis objects
 */
export const getGuestRecentAnalyses = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const analyses = JSON.parse(stored);
    // Sort by most recent first
    return analyses.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  } catch (error) {
    console.error('Error reading guest recent analyses:', error);
    return [];
  }
};

/**
 * Add a recent analysis for guest user
 * @param {Object} analysisData Full analysis response data
 * @param {string} strategy Selected strategy (flip, rental, brrrr)
 * @param {string} arvMode ARV mode used
 * @returns {string} recent_id
 */
export const addGuestRecentAnalysis = (analysisData, strategy = 'flip', arvMode = null) => {
  try {
    const analyses = getGuestRecentAnalyses();
    const address = analysisData.address || '';
    const city = analysisData.city || '';
    const state = analysisData.state || '';
    
    // Check if this property already exists
    const existingIndex = analyses.findIndex(a => 
      a.address === address && a.city === city && a.state === state
    );
    
    const now = new Date().toISOString();
    const recentId = existingIndex >= 0 
      ? analyses[existingIndex].recent_id 
      : `guest_${Date.now()}`;
    
    const recentEntry = {
      recent_id: recentId,
      address,
      city,
      state,
      purchase_price: analysisData.purchase_price || 0,
      arv: analysisData.arv || 0,
      sqft: analysisData.sqft || 0,
      beds: analysisData.beds || 0,
      baths: analysisData.baths || 0,
      strategy,
      arv_mode: arvMode,
      deal_score: analysisData.deal_score || 0,
      deal_rating: analysisData.deal_rating || '',
      net_profit: analysisData.flip_analysis?.net_profit || 0,
      is_saved: false,
      saved_deal_id: null,
      analysis_data: analysisData,
      created_at: existingIndex >= 0 ? analyses[existingIndex].created_at : now,
      updated_at: now
    };
    
    if (existingIndex >= 0) {
      // Update existing entry
      analyses[existingIndex] = recentEntry;
    } else {
      // Add new entry at the beginning
      analyses.unshift(recentEntry);
    }
    
    // Enforce max limit
    const trimmed = analyses.slice(0, MAX_GUEST_ANALYSES);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    return recentId;
    
  } catch (error) {
    console.error('Error adding guest recent analysis:', error);
    return null;
  }
};

/**
 * Update the state of a guest recent analysis
 * @param {string} address Property address
 * @param {string} city City
 * @param {string} state State
 * @param {Object} updates Fields to update
 * @returns {boolean} Success
 */
export const updateGuestRecentState = (address, city, state, updates) => {
  try {
    const analyses = getGuestRecentAnalyses();
    const index = analyses.findIndex(a => 
      a.address === address && a.city === city && a.state === state
    );
    
    if (index < 0) return false;
    
    const allowedFields = ['strategy', 'arv_mode', 'analysis_data', 'deal_score', 
                          'deal_rating', 'net_profit', 'arv', 'purchase_price'];
    
    for (const field of allowedFields) {
      if (field in updates) {
        analyses[index][field] = updates[field];
      }
    }
    analyses[index].updated_at = new Date().toISOString();
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(analyses));
    return true;
    
  } catch (error) {
    console.error('Error updating guest recent state:', error);
    return false;
  }
};

/**
 * Mark a guest recent analysis as saved
 * @param {string} address Property address
 * @param {string} city City
 * @param {string} state State
 * @param {string} savedDealId ID from saved_deals
 * @returns {boolean} Success
 */
export const markGuestRecentAsSaved = (address, city, state, savedDealId) => {
  try {
    const analyses = getGuestRecentAnalyses();
    const index = analyses.findIndex(a => 
      a.address === address && a.city === city && a.state === state
    );
    
    if (index < 0) return false;
    
    analyses[index].is_saved = true;
    analyses[index].saved_deal_id = savedDealId;
    analyses[index].updated_at = new Date().toISOString();
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(analyses));
    return true;
    
  } catch (error) {
    console.error('Error marking guest recent as saved:', error);
    return false;
  }
};

/**
 * Delete a specific guest recent analysis
 * @param {string} recentId Recent analysis ID
 * @returns {boolean} Success
 */
export const deleteGuestRecentAnalysis = (recentId) => {
  try {
    const analyses = getGuestRecentAnalyses();
    const filtered = analyses.filter(a => a.recent_id !== recentId);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
    
  } catch (error) {
    console.error('Error deleting guest recent analysis:', error);
    return false;
  }
};

/**
 * Clear all guest recent analyses
 * @returns {boolean} Success
 */
export const clearGuestRecentAnalyses = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing guest recent analyses:', error);
    return false;
  }
};

/**
 * Migrate guest analyses to authenticated user
 * Called after user logs in to preserve their history
 * @param {Function} addToServerFn Function to add analysis to server
 * @returns {number} Number of migrated analyses
 */
export const migrateGuestAnalysesToServer = async (addToServerFn) => {
  try {
    const guestAnalyses = getGuestRecentAnalyses();
    let migrated = 0;
    
    for (const analysis of guestAnalyses) {
      try {
        await addToServerFn(analysis.analysis_data, analysis.strategy, analysis.arv_mode);
        migrated++;
      } catch (e) {
        console.error('Error migrating analysis:', e);
      }
    }
    
    // Clear guest storage after migration
    if (migrated > 0) {
      clearGuestRecentAnalyses();
    }
    
    return migrated;
    
  } catch (error) {
    console.error('Error migrating guest analyses:', error);
    return 0;
  }
};
